import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, Scale, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';

const Terms = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const sections = [
    {
      icon: Shield,
      title: "1. Acceptance of Terms",
      content: [
        "By accessing and using AstronShield's services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.",
        "If you do not agree with any part of these terms, you may not use our services.",
        "We reserve the right to modify these terms at any time, and your continued use of our services constitutes acceptance of those modifications."
      ]
    },
    {
      icon: Scale,
      title: "2. Service Description",
      content: [
        "AstronShield provides cybersecurity services, including but not limited to:",
        "• Security monitoring and threat detection",
        "• Incident response and management",
        "• Security consulting and advisory services",
        "• Implementation of security solutions",
        "We reserve the right to modify, suspend, or discontinue any part of our services at any time."
      ]
    },
    {
      icon: AlertCircle,
      title: "3. User Responsibilities",
      content: [
        "You agree to:",
        "• Provide accurate and complete information",
        "• Maintain the confidentiality of your account credentials",
        "• Comply with all applicable laws and regulations",
        "• Use our services only for lawful purposes",
        "• Report any security vulnerabilities or breaches immediately"
      ]
    },
    {
      icon: Shield,
      title: "4. Intellectual Property",
      content: [
        "All content, trademarks, and intellectual property on our services are owned by AstronShield.",
        "You may not use, copy, or distribute our content without explicit permission.",
        "Any feedback or suggestions you provide become our property."
      ]
    },
    {
      icon: Scale,
      title: "5. Limitation of Liability",
      content: [
        "AstronShield provides services 'as is' without warranties of any kind.",
        "We are not liable for any damages arising from the use of our services.",
        "Our total liability shall not exceed the amount paid for the services."
      ]
    },
    {
      icon: AlertCircle,
      title: "6. Termination",
      content: [
        "We may terminate or suspend your access to our services at any time for:",
        "• Violation of these terms",
        "• Fraudulent or illegal activities",
        "• Non-payment of fees",
        "• Any other reason we deem appropriate"
      ]
    }
  ];

  const additionalTerms = [
    {
      title: "Confidentiality",
      content: "All information shared during service provision is subject to strict confidentiality requirements."
    },
    {
      title: "Compliance",
      content: "Users must comply with all applicable cybersecurity laws and regulations."
    },
    {
      title: "Updates",
      content: "Terms may be updated periodically. Users will be notified of significant changes."
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <FileText className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Terms of Service</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Last Updated: {currentDate}
          </motion.p>
        </div>

        {/* Main Terms Sections */}
        <div className="space-y-8 mb-20">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <section.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{section.title}</h3>
              </div>
              
              <div className="space-y-4">
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-muted leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Terms */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Additional Terms</h2>
            <p className="text-lg text-muted">Important additional information about our services</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {additionalTerms.map((term, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center mb-4">
                  <CheckCircle className="h-6 w-6 text-primary mr-3" />
                  <h3 className="text-xl font-semibold">{term.title}</h3>
                </div>
                <p className="text-muted">{term.content}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Questions About Our Terms?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              If you have any questions about our Terms of Service, please contact our legal team
            </p>
            <button
              onClick={() => window.location.href = 'mailto:contact@astronshield.com'} 
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;